import React from 'react';
import { MapPin, Users, Check, Filter } from 'lucide-react';
import { interests } from '../data/interests';

const ListView = ({ clusters, currentUser, handleConnectWithUser, showOnlySharedInterests, setShowOnlySharedInterests }) => {
  const totalUsers = clusters.nearby.length + clusters.close.length + clusters.distant.length;
  
  const renderUserCard = (user) => (
    <div key={user.id} className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <img src={user.avatar} alt={user.name} className="w-16 h-16 rounded-full object-cover" />
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="text-xl font-semibold text-gray-900">{user.name}</h3>
              {user.distance !== undefined && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                  {user.distance < 1 ? '<1km' : `${Math.round(user.distance)}km away`}
                </span>
              )}
            </div>
            <p className="text-gray-500 flex items-center mb-3">
              <MapPin className="w-4 h-4 mr-1" />
              {user.location}
            </p>
            <p className="text-gray-700 mb-4">{user.bio}</p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {user.interests.map(interest => {
                const interestData = interests.find(i => i.id === interest);
                if (!interestData) return null;
                const Icon = interestData.icon;
                const hasMatch = currentUser?.interests.includes(interest);
                return (
                  <span
                    key={interest}
                    className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-sm ${
                      hasMatch
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-gray-50 text-gray-600 border border-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{interestData.label}</span>
                    {hasMatch && <Check className="w-3 h-3 ml-1" />}
                  </span>
                );
              })}
            </div>

            {user.socials && (
              <div className="flex space-x-4">
                {user.socials.instagram && (
                  <a href="#" className="text-sm text-gray-500 hover:text-red-600">
                    @{user.socials.instagram}
                  </a>
                )}
                {user.socials.linkedin && (
                  <a href="#" className="text-sm text-gray-500 hover:text-red-600">
                    LinkedIn
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        
        <button 
          onClick={() => handleConnectWithUser(user)}
          className="px-6 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
        >
          Connect
        </button>
      </div>
    </div>
  );
  
  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header with Filter */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">People Near You</h2>
            <p className="text-gray-600">
              Found {totalUsers} {showOnlySharedInterests ? 'people with similar interests' : 'people near you'}, sorted by proximity
            </p>
          </div>
          
          {/* Filter Toggle */}
          <div className="bg-white rounded-xl shadow-lg p-3">
            <button
              onClick={() => setShowOnlySharedInterests(!showOnlySharedInterests)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                showOnlySharedInterests
                  ? 'bg-red-50 text-red-600 border border-red-200'
                  : 'bg-gray-50 text-gray-600 border border-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">
                {showOnlySharedInterests ? 'Shared Interests' : 'Everyone'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Nearby Users (0-10km) */}
      {clusters.nearby.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              Very Close ({clusters.nearby.length})
            </h3>
            <span className="text-sm text-gray-500">Within 10km</span>
          </div>
          <div className="space-y-4">
            {clusters.nearby.map(renderUserCard)}
          </div>
        </div>
      )}

      {/* Close Users (10-30km) */}
      {clusters.close.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              Nearby ({clusters.close.length})
            </h3>
            <span className="text-sm text-gray-500">10-30km away</span>
          </div>
          <div className="space-y-4">
            {clusters.close.map(renderUserCard)}
          </div>
        </div>
      )}

      {/* Distant Users (30km+) */}
      {clusters.distant.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              Further Away ({clusters.distant.length})
            </h3>
            <span className="text-sm text-gray-500">30km+ away</span>
          </div>
          <div className="space-y-4">
            {clusters.distant.map(renderUserCard)}
          </div>
        </div>
      )}

      {totalUsers === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No matches found</h3>
          <p className="text-gray-500">Try adjusting your interests or location preferences.</p>
        </div>
      )}
    </div>
  );
};

export default ListView;
